import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '@/styles/notfound.css';

const NAVY = '#16263D';
const GOLD = '#A9802F';
const TEAL = '#1FA6CC';
const PAPER = '#FAFAF7';
const MONO = "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const SERIF = "'Newsreader', Georgia, 'Times New Roman', serif";
const SANS = "'Public Sans', system-ui, -apple-system, 'Segoe UI', sans-serif";

// floating background tax glyphs: [char, left%, top%, size, color, peakOpacity, dur, delay]
const FLOATERS = [
  ['$', 86, 28, 30, TEAL, 0.55, 13, 0],
  ['§', 70, 84, 34, TEAL, 0.5, 15, 2.4],
  ['%', 18, 90, 28, GOLD, 0.4, 14, 1.1],
  ['1040', 12, 33, 17, NAVY, 0.12, 17, 3.2],
  ['990', 90, 78, 18, '#2F7D5B', 0.5, 16, 0.9],
  ['W-2', 58, 90, 18, GOLD, 0.4, 15, 4],
  ['¶', 8, 60, 26, NAVY, 0.12, 16, 2 ],
  ['¢', 46, 88, 22, NAVY, 0.1, 14, 1.7],
  ['÷', 80, 92, 22, NAVY, 0.1, 15, 3.6],
  ['$', 7, 80, 30, NAVY, 0.12, 14, 0.4],
];

const RECEIPT_ROWS = [
  ['Pages audited', '1,040'],
  ['Refunds located', '0'],
  ['Deductible humor', 'No'],
  ['Late penalty', '$0.00'],
];

const NotFound = () => (
  <div className="kg404" data-testid="not-found-page" style={{ background: PAPER, minHeight: '100vh', color: NAVY, fontFamily: SANS, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
    <Helmet>
      <title>404 — Page not found | KGOB CPAs &amp; Advisors</title>
        <meta name="description" content="The page you are looking for could not be found. Explore KGOB CPA services, locations, and resources." />
      <meta name="robots" content="noindex,follow" />
      <link rel="stylesheet" href="/notfound-fonts/fonts.css" />
    </Helmet>

    {/* floating background glyphs */}
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
      {FLOATERS.map((f, i) => (
        <span key={i} style={{
          position: 'absolute', left: `${f[1]}%`, top: `${f[2]}%`, fontFamily: f[0].length > 1 ? MONO : SANS,
          fontWeight: 700, fontSize: f[3], color: f[4], '--peak': f[5], opacity: 0,
          animation: `kg404-float ${f[6]}s ease-in-out ${f[7]}s infinite`,
        }}>{f[0]}</span>
      ))}
    </div>

    {/* header */}
    <header className="kg-top" style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '30px 56px' }}>
      <Link to="/" data-testid="not-found-logo-link" aria-label="KGOB home">
        <img className="kg-logo" src="/brand/kgob-logo-light.svg" alt="Kohari Gonzalez Oneyear &amp; Brown — CPAs &amp; Advisors" style={{ height: 56, width: 'auto', display: 'block' }} />
      </Link>
      <span className="kg-tag" style={{ fontFamily: MONO, fontSize: 12.5, letterSpacing: '.22em', color: NAVY, opacity: 0.65 }}>LET'S TALK GROWTH&reg;</span>
    </header>

    {/* main */}
    <main className="kg-main" style={{ position: 'relative', zIndex: 1, flex: 1, display: 'grid', gridTemplateColumns: '1.05fr .95fr', alignItems: 'center', gap: 64, padding: '8px 56px 64px', maxWidth: 1240, width: '100%', margin: '0 auto' }}>
      {/* left column */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
          <span style={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, letterSpacing: '.2em', color: GOLD }}>ERROR &middot; FORM 1040-EZ</span>
          <span style={{ width: 48, height: 1.5, background: GOLD, opacity: 0.6 }} />
        </div>

        {/* 404 with spinning coin */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
          <span className="kg-digit" style={{ fontFamily: SANS, fontWeight: 800, fontSize: 150, lineHeight: 1, color: NAVY, letterSpacing: '-4px' }}>4</span>
          <span style={{ perspective: 600, margin: '0 2px' }}>
            <span className="kg-coin" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 116, height: 116, borderRadius: '50%', background: 'radial-gradient(circle at 38% 32%, #E9C766 0%, #C9A23F 42%, #9A7220 100%)', boxShadow: 'inset 0 4px 10px rgba(255,255,255,.45), inset 0 -6px 12px rgba(90,60,10,.4), 0 12px 26px rgba(154,114,32,.4)', border: `2px solid ${GOLD}`, transformStyle: 'preserve-3d', animation: 'kg404-coin 4.5s linear infinite' }}>
              <span className="kg-coin-glyph" style={{ fontFamily: SANS, fontWeight: 800, fontSize: 62, color: '#FBF3DC', textShadow: '0 1px 1px rgba(90,60,10,.55)' }}>$</span>
            </span>
          </span>
          <span className="kg-digit" style={{ fontFamily: SANS, fontWeight: 800, fontSize: 150, lineHeight: 1, color: NAVY, letterSpacing: '-4px' }}>4</span>
        </div>

        <h1 className="kg-h1" style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 56, lineHeight: 1.04, color: NAVY, margin: '14px 0 18px', letterSpacing: '-.5px' }}>
          This page filed<br />for an extension.
        </h1>

        <p style={{ fontFamily: SANS, fontSize: 17, lineHeight: 1.62, color: '#48566A', maxWidth: 430, margin: '0 0 30px' }}>
          We audited every directory, spreadsheet, and shoebox of receipts — this URL is nowhere on the books. Consider it a write-off and let's get you back to something deductible.
        </p>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <Link to="/" data-testid="not-found-home-btn" className="scp0" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: NAVY, color: PAPER, fontFamily: SANS, fontWeight: 600, fontSize: 15.5, padding: '15px 26px', borderRadius: 6, textDecoration: 'none' }}>
            &larr;&nbsp;Back to safe harbor
          </Link>
          <Link to="/contact" data-testid="not-found-cpa-btn" className="scp1" style={{ display: 'inline-flex', alignItems: 'center', background: 'transparent', color: GOLD, fontFamily: SANS, fontWeight: 700, fontSize: 15.5, padding: '15px 26px', borderRadius: 6, textDecoration: 'none', border: `1.5px solid ${GOLD}` }}>
            Talk to a real CPA
          </Link>
        </div>

        <div style={{ marginTop: 26, fontFamily: MONO, fontSize: 13.5, color: '#6B7686' }}>
          <span style={{ color: GOLD }}>&gt;</span> grep -r <span style={{ color: NAVY }}>"this-page"</span> /server&nbsp;&nbsp;<span style={{ color: '#9AA3B0' }}>0 matches found</span>
          <span style={{ display: 'inline-block', width: 8, height: 16, background: NAVY, marginLeft: 4, verticalAlign: 'middle', animation: 'kg404-blink 1.1s step-end infinite' }} />
        </div>
      </div>

      {/* right column — animated audit receipt + magnifier */}
      <div className="kg-receipt-wrap" style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 420 }}>
        {/* magnifier */}
        <div className="kg-mag" aria-hidden="true" style={{ position: 'absolute', left: 12, top: 18, zIndex: 3, animation: 'kg404-scan 5s ease-in-out infinite' }}>
          <svg width="58" height="58" viewBox="0 0 58 58" fill="none" stroke={NAVY} strokeWidth="4">
            <circle cx="23" cy="23" r="17" fill="rgba(31,166,204,.08)" />
            <line x1="35" y1="35" x2="52" y2="52" strokeLinecap="round" />
          </svg>
        </div>

        <div className="kg-receipt" style={{ position: 'relative', width: 320, background: '#fff', borderRadius: 6, padding: '30px 30px 16px', boxShadow: '0 26px 60px rgba(22,38,61,.18)', animation: 'kg404-receipt 5.5s ease-in-out infinite', transform: 'rotate(-3deg)' }}>
          {/* PAGE NOT FOUND stamp */}
          <div aria-hidden="true" style={{ position: 'absolute', top: 78, left: 26, zIndex: 4, color: '#C0392B', border: '3px solid #C0392B', borderRadius: 6, padding: '8px 14px', fontFamily: SANS, fontWeight: 800, fontSize: 19, lineHeight: 1.05, letterSpacing: '.5px', textAlign: 'center', opacity: 0.92, animation: 'kg404-stamp 1s cubic-bezier(.2,.9,.3,1.2) .6s both' }}>
            PAGE<br />NOT&nbsp;FOUND
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontFamily: MONO, marginBottom: 6 }}>
            <span style={{ fontWeight: 600, fontSize: 15, letterSpacing: '.05em', color: NAVY }}>KGOB &middot; AUDIT</span>
            <span style={{ fontSize: 11, color: '#9AA3B0' }}>NO. 000-404</span>
          </div>
          <div style={{ borderTop: '1px dashed #D7DBE2', margin: '6px 0 12px' }} />

          {RECEIPT_ROWS.map((r) => (
            <div key={r[0]} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: MONO, fontSize: 13, padding: '5px 0', color: '#5A6676' }}>
              <span>{r[0]}</span><span style={{ color: NAVY, fontWeight: 500 }}>{r[1]}</span>
            </div>
          ))}

          <div style={{ borderTop: '1px dashed #D7DBE2', margin: '12px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: MONO, fontSize: 12.5, color: '#5A6676', padding: '3px 0' }}>
            <span>STATUS</span><span style={{ color: '#C0392B', fontWeight: 600 }}>NOT FOUND</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 6 }}>
            <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 600, color: NAVY }}>TOTAL DUE</span>
            <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 18, color: GOLD }}>A good laugh</span>
          </div>

          {/* barcode */}
          <div aria-hidden="true" style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 30, margin: '16px 0 10px' }}>
            {[6,2,4,2,3,6,2,5,3,2,4,6,2,3].map((w, i) => (
              <span key={i} style={{ width: w, height: '100%', background: NAVY }} />
            ))}
          </div>
          <div style={{ textAlign: 'center', fontFamily: MONO, fontSize: 10.5, letterSpacing: '.18em', color: '#9AA3B0' }}>THANK YOU FOR FILING</div>
        </div>
      </div>
    </main>

    {/* footer */}
    <footer className="kg-foot" style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 56px 28px', fontFamily: MONO, fontSize: 11.5, letterSpacing: '.1em', color: '#9AA3B0' }}>
      <span>HTTP 404 &middot; resource not found</span>
      <span className="kg-foot-2">Kohari Gonzalez Oneyear &amp; Brown &middot; CPAs &amp; Advisors</span>
    </footer>
  </div>
);

export default NotFound;
